class FormWaitlist extends HTMLElement {
  constructor() {
    super();
    this.klaviyoForm = null;
    this.productData = {
      url: '',
      title: '',
      variantId: ''
    };
    this.variantSelects = null;
  }

  connectedCallback() {
    // Get product data from the page
    this.initializeProductData();
    
    // Listen for variant changes
    this.setupVariantListener();
    
    // Wait for Klaviyo form to load
    this.waitForKlaviyoForm();
    
    // Also fill fields when modal opens
    this.setupModalListener();
  }

  setupModalListener() {
    // Find the modal dialog
    const modal = this.closest('modal-dialog');
    if (modal) {
      // Listen for when modal is shown (when 'open' attribute is added)
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            // Modal opened, wait a bit for Klaviyo form to render, then fill fields
            setTimeout(() => {
              this.waitForKlaviyoForm();
              this.fillFormFields();
            }, 500);
          }
        });
      });
      
      observer.observe(modal, {
        attributes: true,
        attributeFilter: ['open']
      });
    }
  }

  waitForKlaviyoForm() {
    // If form already found, just fill it
    if (this.klaviyoForm && document.contains(this.klaviyoForm)) {
      this.fillFormFields();
      return;
    }

    // Klaviyo forms are loaded dynamically, so we need to wait for them
    let checkCount = 0;
    const maxChecks = 100; // Check for up to 10 seconds (100 * 100ms)
    
    const checkInterval = setInterval(() => {
      checkCount++;
      
      // Look for Klaviyo form
      const klaviyoContainer = this.querySelector('.klaviyo-form-R5TugX');
      if (!klaviyoContainer) {
        if (checkCount >= maxChecks) clearInterval(checkInterval);
        return;
      }

      // Try to find the actual form element
      // Klaviyo forms can be in iframes or directly in the container
      let form = klaviyoContainer.querySelector('form');

      if (form) {
        clearInterval(checkInterval);
        this.klaviyoForm = form;
        this.setupFormListener();
        this.fillFormFields();
      } else if (checkCount >= maxChecks) {
        clearInterval(checkInterval);
        console.warn('FormWaitlist: Klaviyo form not found after waiting');
      }
    }, 100);
  }

  initializeProductData() {
    // Get product data from data attributes (most reliable)
    this.productData.url = this.getAttribute('data-product-url') || window.location.origin + window.location.pathname;
    this.productData.title = this.getAttribute('data-product-title') || '';
    this.productData.variantId = this.getAttribute('data-variant-id') || '';

    // If not in data attributes, try to get from page
    if (!this.productData.title) {
      const productTitleEl = document.querySelector('h1.product__title, .product__title, [data-product-title]');
      if (productTitleEl) {
        this.productData.title = productTitleEl.textContent.trim();
      } else {
        // Fallback: try to get from meta tags
        const metaTitle = document.querySelector('meta[property="og:title"]');
        if (metaTitle) {
          this.productData.title = metaTitle.getAttribute('content');
        }
      }
    }

    // Get current variant ID (will be updated when variant changes)
    this.updateVariantId();
  }

  updateVariantId() {
    // Try to get variant ID from the product form
    const variantInput = document.querySelector('input[name="id"][data-productid]');
    if (variantInput && variantInput.value) {
      this.productData.variantId = variantInput.value;
      return;
    }

    // Try to get from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      this.productData.variantId = variantParam;
      return;
    }

    // Try to get from variant-selects component
    if (this.variantSelects && this.variantSelects.currentVariant) {
      this.productData.variantId = this.variantSelects.currentVariant.id.toString();
    }
  }

  setupVariantListener() {
    // Find variant selector component
    this.variantSelects = document.querySelector('variant-selects, variant-radios');
    
    if (this.variantSelects) {
      // Listen for variant change events
      this.variantSelects.addEventListener('change', () => {
        this.updateVariantId();
        this.fillFormFields();
      });

      // Also listen for custom variant change events that might be dispatched
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this.productData.variantId = e.detail.variant.id.toString();
          this.fillFormFields();
        }
      });
    }

    // Listen for changes to the variant input
    const variantInput = document.querySelector('input[name="id"][data-productid]');
    if (variantInput) {
      const observer = new MutationObserver(() => {
        this.updateVariantId();
        this.fillFormFields();
      });
      
      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      // Also listen for input events
      variantInput.addEventListener('change', () => {
        this.updateVariantId();
        this.fillFormFields();
      });
    }
  }

  setupFormListener() {
    if (!this.klaviyoForm) return;

    // Fill fields before form submission and validate checkbox
    const button = this.klaviyoForm.querySelector('button[type="button"]');

    button.addEventListener('click', (e) => {
      // Fill fields first
      this.fillFormFields();
      
      // Validate checkbox
      const checkbox = this.findCheckbox();
      if (checkbox && !checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        
        return false;
      }
    }, true); // Use capture phase to fill before Klaviyo processes

    // Also fill on any input focus (user might interact with form)
    const inputs = this.klaviyoForm.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        this.fillFormFields();
      });
    });
  }

  findCheckbox() {
    // Try to find form first
    if (!this.klaviyoForm) {
      const klaviyoContainer = this.querySelector('.klaviyo-form-R5TugX');
      if (klaviyoContainer) {
        this.klaviyoForm = klaviyoContainer.querySelector('form');
      }
    }

    if (!this.klaviyoForm) {
      // Search in the entire Klaviyo container
      const klaviyoContainer = this.querySelector('.klaviyo-form-R5TugX');
      if (klaviyoContainer) {
        const checkbox = klaviyoContainer.querySelector('input[type="checkbox"]');
        if (checkbox) return checkbox;
      }
      return null;
    }

    // Search in the form
    let checkbox = this.klaviyoForm.querySelector('input[type="checkbox"]');
    
    // If not found, search in the entire Klaviyo container
    if (!checkbox) {
      const klaviyoContainer = this.querySelector('.klaviyo-form-R5TugX');
      if (klaviyoContainer) {
        checkbox = klaviyoContainer.querySelector('input[type="checkbox"]');
      }
    }

    return checkbox || null;
  }

  fillFormFields() {
    if (!this.klaviyoForm) {
      // Try to find form again
      const klaviyoContainer = this.querySelector('.klaviyo-form-R5TugX');
      if (klaviyoContainer) {
        this.klaviyoForm = klaviyoContainer.querySelector('form');
      }
      if (!this.klaviyoForm) return;
    }

    // Find fields by placeholder attribute
    const fields = {
      product_url: this.findFieldByPlaceholder('product_url'),
      product_title: this.findFieldByPlaceholder('product_title'),
      variant_id: this.findFieldByPlaceholder('variant_id')
    };

    // Fill each field if found
    if (fields.product_url) {
      fields.product_url.value = this.productData.url;
      // Trigger input event for Klaviyo to detect the change
      fields.product_url.dispatchEvent(new Event('input', { bubbles: true }));
      fields.product_url.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (fields.product_title) {
      fields.product_title.value = this.productData.title;
      fields.product_title.dispatchEvent(new Event('input', { bubbles: true }));
      fields.product_title.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (fields.variant_id && this.productData.variantId) {
      fields.variant_id.value = this.productData.variantId;
      fields.variant_id.dispatchEvent(new Event('input', { bubbles: true }));
      fields.variant_id.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  findFieldByPlaceholder(placeholderValue) {
    if (!this.klaviyoForm) {
      // Try to find form in the container
      const klaviyoContainer = this.querySelector('.klaviyo-form-R5TugX');
      if (klaviyoContainer) {
        this.klaviyoForm = klaviyoContainer.querySelector('form');
      }
    }

    if (!this.klaviyoForm) return null;

    // Search in the form
    let field = Array.from(this.klaviyoForm.querySelectorAll('input, textarea, select')).find(
      el => el.getAttribute('placeholder') === placeholderValue
    );

    // If not found, search in the entire Klaviyo container (form might be outside the form element)
    if (!field) {
      const klaviyoContainer = this.querySelector('.klaviyo-form-R5TugX');
      if (klaviyoContainer) {
        field = Array.from(klaviyoContainer.querySelectorAll('input, textarea, select')).find(
          el => el.getAttribute('placeholder') === placeholderValue
        );
      }
    }

    // Debug: log if field not found
    if (!field) {
      console.warn(`FormWaitlist: Field with placeholder "${placeholderValue}" not found`);
    }

    return field || null;
  }
}

// Register the custom element
customElements.define('form-waitlist', FormWaitlist);
